import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Settings as SettingsIcon, Clock } from 'lucide-react';

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [intervalMinutes, setIntervalMinutes] = useState(5);

  useEffect(() => {
    fetchSettings();
    const handler = () => {
      setLoading(true);
      fetchSettings();
    };
    window.addEventListener('team-context-changed', handler);
    return () => window.removeEventListener('team-context-changed', handler);
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
      setIntervalMinutes(response.data.screenshot_interval_minutes);
    } catch (error) {
      toast.error('Gagal memuat settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const parsed = parseInt(intervalMinutes, 10);
    if (Number.isNaN(parsed) || parsed < 1 || parsed > 60) {
      toast.error('Interval harus angka antara 1-60 menit');
      return;
    }

    setSaving(true);
    try {
      await api.put('/settings', {
        screenshot_interval_minutes: parsed
      });
      toast.success('Settings berhasil disimpan!');
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A3C34]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Header — samain sama Employees Management */}
      <div className="pb-2 border-b border-slate-200/70">
        <h1 className="text-3xl lg:text-4xl font-bold font-heading text-slate-900 tracking-tight" data-testid="settings-page-title">
          Settings
        </h1>
        <p className="text-muted-foreground text-sm mt-1">Konfigurasi aplikasi desktop</p>
      </div>

      <Card className="p-6 bg-white border-slate-200/80 shadow-sm">
        <div className="pb-4 border-b border-slate-100 mb-6 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#1A3C34]/10 text-[#1A3C34] flex items-center justify-center">
            <SettingsIcon className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-xl font-bold font-heading text-slate-900">Desktop App Configuration</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Settings yang berlaku untuk semua karyawan</p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="space-y-3">
            <Label htmlFor="screenshot-interval" className="text-sm font-medium text-slate-700">
              Screenshot Interval
            </Label>
            <p className="text-xs text-muted-foreground">
              Frekuensi pengambilan screenshot otomatis di aplikasi desktop
            </p>

            <div className="flex items-center gap-4 max-w-md">
              <div className="flex items-center gap-2 flex-1">
                <Clock className="w-4 h-4 text-slate-400" />
                <Input
                  id="screenshot-interval"
                  data-testid="screenshot-interval-input"
                  type="number"
                  min="1"
                  max="60"
                  value={intervalMinutes}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '') {
                      setIntervalMinutes('');
                    } else {
                      const num = parseInt(val, 10);
                      setIntervalMinutes(Number.isNaN(num) ? '' : String(num));
                    }
                  }}
                  className="w-24 border-slate-200 rounded-xl text-sm focus-visible:ring-[#1A3C34] focus-visible:border-[#1A3C34]"
                />
                <span className="text-sm text-slate-600">menit</span>
              </div>
            </div>

            <div className="bg-[#1A3C34]/5 border border-[#1A3C34]/10 p-4 rounded-xl mt-4">
              <p className="text-sm text-slate-700">
                <strong>Current setting:</strong> Screenshot setiap{' '}
                <strong>{settings?.screenshot_interval_minutes ?? intervalMinutes ?? '-'} menit</strong>
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Setting ini akan berlaku untuk semua karyawan yang menggunakan aplikasi desktop.
                Aplikasi akan otomatis menyesuaikan interval setelah restart atau refresh.
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={handleSave}
              data-testid="save-settings-button"
              disabled={saving}
              className="bg-[#1A3C34] hover:bg-[#142E27] text-white font-medium rounded-xl shadow-md transition-all duration-200 active:scale-[0.99]"
            >
              {saving ? 'Menyimpan...' : 'Simpan Settings'}
            </Button>
            <Button
              onClick={fetchSettings}
              variant="outline"
              className="rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-800"
            >
              Reset
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-6 bg-white border-slate-200/80 shadow-sm">
        <div className="pb-4 border-b border-slate-100 mb-6">
          <h3 className="text-xl font-bold font-heading text-slate-900">Desktop App Features Info</h3>
        </div>
        <div className="space-y-3 text-sm">
          <div className="flex items-start gap-3">
            <div className="w-2 h-2 rounded-full bg-[#1A3C34] mt-2"></div>
            <div>
              <p className="font-semibold text-slate-900">Auto Screenshot</p>
              <p className="text-muted-foreground">Desktop app akan otomatis capture screenshot sesuai interval yang dikonfigurasi</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-2 h-2 rounded-full bg-[#1A3C34] mt-2"></div>
            <div>
              <p className="font-semibold text-slate-900">Cursor Activity Monitoring</p>
              <p className="text-muted-foreground">Track persentase idle dan active dari mouse/keyboard activity</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-2 h-2 rounded-full bg-[#1A3C34] mt-2"></div>
            <div>
              <p className="font-semibold text-slate-900">IP-based Location</p>
              <p className="text-muted-foreground">Detect lokasi berdasarkan IP address karyawan</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-2 h-2 rounded-full bg-[#1A3C34] mt-2"></div>
            <div>
              <p className="font-semibold text-slate-900">System Tray & Auto-start</p>
              <p className="text-muted-foreground">Aplikasi berjalan di background dan auto-start saat booting</p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}